import { useEffect, useState } from "react";
import { useUiContextStore } from "../../stores/uiContextStore";
import { Plus, Scale } from "lucide-react";
import { DataTable, Column } from "../components/DataTable";
import { StatusBadge } from "../components/StatusBadge";
import { WorkflowStepper } from "../components/WorkflowStepper";
import {
  Modal,
  FormField,
  FormInput,
  FormSelect,
  FormTextarea,
  ModalFooterButtons,
} from "../components/Modal";
import { formatVersion } from "../../utils/formatters";

type GovTab = "policies" | "workflows" | "delegation" | "changes";

/* ── Policy lifecycle states (REQ-2.2) ───────────────────── */
type PolicyStatus =
  | "draft"
  | "in_review"
  | "approved"
  | "published"
  | "retired";
const policyStatusColor: Record<PolicyStatus, string> = {
  draft: "bg-slate-100 dark:bg-white/[0.07] text-slate-600 dark:text-white/50",
  in_review:
    "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400",
  approved:
    "bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400",
  published:
    "bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400",
  retired:
    "bg-slate-100 dark:bg-white/[0.05] text-slate-400 dark:text-white/30",
};
const policyStatusLabel: Record<PolicyStatus, string> = {
  draft: "Draft",
  in_review: "In Review",
  approved: "Approved",
  published: "Published",
  retired: "Retired",
};

const policies = [
  {
    id: "pol1",
    name: "Information Security Policy",
    version: "3.2",
    owner: "CISO",
    reviewDate: "2026-12-01",
    lifecycle: "published" as PolicyStatus,
    approver: "Dr. C. Moore",
    controlGap: 2,
  },
  {
    id: "pol2",
    name: "Access Control Policy",
    version: "2.1",
    owner: "IT Security",
    reviewDate: "2026-09-15",
    lifecycle: "published" as PolicyStatus,
    approver: "Alex Johnson",
    controlGap: 0,
  },
  {
    id: "pol3",
    name: "Incident Response Policy",
    version: "1.8",
    owner: "IR Team",
    reviewDate: "2026-11-01",
    lifecycle: "approved" as PolicyStatus,
    approver: "Sarah Kim",
    controlGap: 1,
  },
  {
    id: "pol4",
    name: "Configuration Management Policy",
    version: "2.0",
    owner: "Operations",
    reviewDate: "2026-08-01",
    lifecycle: "in_review" as PolicyStatus,
    approver: "Marcus Davis",
    controlGap: 3,
  },
  {
    id: "pol5",
    name: "Data Classification Policy",
    version: "1.5",
    owner: "Privacy Team",
    reviewDate: "2026-07-01",
    lifecycle: "draft" as PolicyStatus,
    approver: "Dr. C. Moore",
    controlGap: 5,
  },
  {
    id: "pol6",
    name: "Vulnerability Management Policy",
    version: "2.3",
    owner: "Security",
    reviewDate: "2026-10-15",
    lifecycle: "retired" as PolicyStatus,
    approver: "Sarah Kim",
    controlGap: 0,
  },
];

const workflows = [
  {
    id: "wf1",
    name: "SSP Change Approval",
    trigger: "SSP modification",
    steps: 4,
    approvers: ["ISSO", "System Owner", "AO"],
    status: "active",
    pending: 2,
  },
  {
    id: "wf2",
    name: "POAM Closure Review",
    trigger: "POAM status change to Resolved",
    steps: 3,
    approvers: ["ISSO", "AO"],
    status: "active",
    pending: 1,
  },
  {
    id: "wf3",
    name: "Policy Annual Review",
    trigger: "Annual schedule / policy update",
    steps: 5,
    approvers: ["Owner", "Legal", "CISO", "AO"],
    status: "active",
    pending: 0,
  },
  {
    id: "wf4",
    name: "Emergency Change Approval",
    trigger: "Emergency change request",
    steps: 2,
    approvers: ["ISSO", "System Owner"],
    status: "active",
    pending: 0,
  },
];

const changeRequests = [
  {
    id: "cr1",
    title: "Add new EC2 instance to production subnet",
    requester: "Marcus Davis",
    type: "Configuration",
    risk: "low",
    status: "pending",
    submitted: "2026-03-08",
  },
  {
    id: "cr2",
    title: "Update IAM policy for analytics service account",
    requester: "Alex Johnson",
    type: "Access Control",
    risk: "medium",
    status: "in-progress",
    submitted: "2026-03-07",
  },
  {
    id: "cr3",
    title: "Deploy new Vault secrets engine for database credentials",
    requester: "Sarah Kim",
    type: "Security",
    risk: "medium",
    status: "pending",
    submitted: "2026-03-06",
  },
  {
    id: "cr4",
    title: "Modify network ACL to allow outbound HTTPS to vendor API",
    requester: "Marcus Davis",
    type: "Network",
    risk: "low",
    status: "active",
    submitted: "2026-03-05",
  },
  {
    id: "cr5",
    title: "Upgrade PostgreSQL to 16.2 on production RDS",
    requester: "Database Team",
    type: "Maintenance",
    risk: "high",
    status: "in-progress",
    submitted: "2026-03-04",
  },
];

const delegations = [
  {
    id: "del1",
    role: "Authorizing Official (AO)",
    delegatee: "Dr. Christine Moore",
    delegator: "Agency Head",
    scope: "Full authorization authority",
    expires: "2027-01-01",
    status: "active",
  },
  {
    id: "del2",
    role: "ISSO",
    delegatee: "Alex Johnson",
    delegator: "System Owner",
    scope: "Day-to-day security operations",
    expires: "2026-12-31",
    status: "active",
  },
  {
    id: "del3",
    role: "Assessment Lead",
    delegatee: "External - Coalfire",
    delegator: "AO",
    scope: "FY2026 Q1 Assessment",
    expires: "2026-06-30",
    status: "active",
  },
  {
    id: "del4",
    role: "System Owner (Backup)",
    delegatee: "Sarah Kim",
    delegator: "System Owner",
    scope: "Emergency backup authorization",
    expires: "2026-12-31",
    status: "active",
  },
];

const policyCols: Column<(typeof policies)[0]>[] = [
  {
    key: "name",
    header: "Policy Name",
    sortable: true,
    render: (row) => <>{row.name}</>,
  },
  {
    key: "version",
    header: "Version",
    render: (row) => <>{formatVersion(row.version)}</>,
  },
  { key: "owner", header: "Owner", render: (row) => <>{row.owner}</> },
  {
    key: "reviewDate",
    header: "Next Review",
    sortable: true,
    render: (row) => <>{row.reviewDate}</>,
  },
  { key: "approver", header: "Approver", render: (row) => <>{row.approver}</> },
  {
    key: "lifecycle",
    header: "Lifecycle Status",
    render: (row) => (
      <span
        className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${policyStatusColor[row.lifecycle]}`}
      >
        {policyStatusLabel[row.lifecycle]}
      </span>
    ),
  },
  {
    key: "controlGap",
    header: "Control Gaps",
    render: (row) =>
      row.controlGap > 0 ? (
        <span className="text-xs font-medium text-red-600 dark:text-red-400">
          {row.controlGap} gaps
        </span>
      ) : (
        <span className="text-xs text-emerald-600 dark:text-emerald-400">
          None
        </span>
      ),
  },
];

const crCols: Column<(typeof changeRequests)[0]>[] = [
  {
    key: "id",
    header: "ID",
    render: (row) => <span className="font-mono text-xs">{row.id}</span>,
  },
  {
    key: "title",
    header: "Title",
    sortable: true,
    render: (row) => <>{row.title}</>,
  },
  {
    key: "requester",
    header: "Requester",
    render: (row) => <>{row.requester}</>,
  },
  { key: "type", header: "Type", render: (row) => <>{row.type}</> },
  {
    key: "risk",
    header: "Risk",
    render: (row) => (
      <StatusBadge status={row.risk as "low" | "medium" | "high"} />
    ),
  },
  {
    key: "status",
    header: "Status",
    render: (row) => (
      <StatusBadge
        status={row.status as "pending" | "in-progress" | "active"}
      />
    ),
  },
  {
    key: "submitted",
    header: "Submitted",
    render: (row) => <>{row.submitted}</>,
  },
];

const workflowSteps = [
  { id: "s1", label: "Submit" },
  { id: "s2", label: "ISSO Review" },
  { id: "s3", label: "Owner Approval" },
  { id: "s4", label: "AO Authorization" },
];

export function Governance() {
  const [activeTab, setActiveTab] = useState<GovTab>("policies");

  // Publish the open tab so the AI Companion can offer questions about it. All four tabs share one
  // route, so the tab is the only signal that separates "what is waiting for approval" from "who can
  // approve while the owner is away". Keys match governance.yaml's view blocks. Cleared on unmount.
  useEffect(() => {
    useUiContextStore
      .getState()
      .setView(activeTab.toUpperCase() as "POLICIES" | "WORKFLOWS" | "DELEGATION" | "CHANGES");
    return () => useUiContextStore.getState().clearPageContext();
  }, [activeTab]);

  // Modal states
  const [showNewPolicy, setShowNewPolicy] = useState(false);
  const [showNewWorkflow, setShowNewWorkflow] = useState(false);
  const [showNewDelegation, setShowNewDelegation] = useState(false);
  const [showNewChange, setShowNewChange] = useState(false);

  const [policyForm, setPolicyForm] = useState({
    name: "",
    version: "",
    owner: "",
    approver: "",
    reviewDate: "",
  });
  const [workflowForm, setWorkflowForm] = useState({
    name: "",
    trigger: "",
    approvers: "",
  });
  const [delegationForm, setDelegationForm] = useState({
    role: "",
    delegatee: "",
    delegator: "",
    scope: "",
    expires: "",
  });
  const [changeForm, setChangeForm] = useState({
    title: "",
    type: "",
    risk: "",
    requester: "",
    description: "",
  });

  // Sort state – policies table
  const [polSortKey, setPolSortKey] = useState<string | null>(null);
  const [polSortDir, setPolSortDir] = useState<"asc" | "desc">("asc");
  const handlePolSort = (key: string) => {
    if (polSortKey === key)
      setPolSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setPolSortKey(key);
      setPolSortDir("asc");
    }
  };
  const PolSortIcon = ({ col }: { col: string }) => {
    const active = polSortKey === col;
    return (
      <span
        className="inline-flex flex-col items-center ml-2 select-none"
        style={{ fontSize: "7px", lineHeight: "7px" }}
      >
        <span
          className={`font-black ${active && polSortDir === "asc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
        >
          ▲
        </span>
        <span
          className={`font-black ${active && polSortDir === "desc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
        >
          ▼
        </span>
      </span>
    );
  };

  // Sort state – change requests table
  const [crSortKey, setCrSortKey] = useState<string | null>(null);
  const [crSortDir, setCrSortDir] = useState<"asc" | "desc">("asc");
  const handleCrSort = (key: string) => {
    if (crSortKey === key) setCrSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setCrSortKey(key);
      setCrSortDir("asc");
    }
  };
  const CrSortIcon = ({ col }: { col: string }) => {
    const active = crSortKey === col;
    return (
      <span
        className="inline-flex flex-col items-center ml-2 select-none"
        style={{ fontSize: "7px", lineHeight: "7px" }}
      >
        <span
          className={`font-black ${active && crSortDir === "asc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
        >
          ▲
        </span>
        <span
          className={`font-black ${active && crSortDir === "desc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
        >
          ▼
        </span>
      </span>
    );
  };

  const sortedPolicies = [...policies].sort((a, b) => {
    if (!polSortKey) return 0;
    const aVal: any = a[polSortKey as keyof typeof a];
    const bVal: any = b[polSortKey as keyof typeof a];
    if (typeof aVal === "number")
      return polSortDir === "asc" ? aVal - bVal : bVal - aVal;
    return polSortDir === "asc"
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });

  const sortedCr = [...changeRequests].sort((a, b) => {
    if (!crSortKey) return 0;
    const aVal: any = a[crSortKey as keyof typeof a];
    const bVal: any = b[crSortKey as keyof typeof a];
    if (typeof aVal === "number")
      return crSortDir === "asc" ? aVal - bVal : bVal - aVal;
    return crSortDir === "asc"
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });

  const sortedPolicyCols: Column<(typeof policies)[0]>[] = [
    {
      key: "name",
      align: "left",
      header: (
        <button
          onClick={() => handlePolSort("name")}
          className="flex w-full items-center justify-center font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
        >
          Policy Name
          <PolSortIcon col="name" />
        </button>
      ),
      render: (row) => <>{row.name}</>,
    },
    {
      key: "version",
      header: "Version",
      align: "center",
      render: (row) => <>{formatVersion(row.version)}</>,
    },
    {
      key: "owner",
      header: "Owner",
      align: "left",
      render: (row) => <>{row.owner}</>,
    },
    {
      key: "reviewDate",
      align: "center",
      header: (
        <button
          onClick={() => handlePolSort("reviewDate")}
          className="flex w-full items-center justify-center font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
        >
          Next Review
          <PolSortIcon col="reviewDate" />
        </button>
      ),
      render: (row) => <>{row.reviewDate}</>,
    },
    {
      key: "approver",
      header: "Approver",
      align: "left",
      render: (row) => <>{row.approver}</>,
    },
    {
      key: "lifecycle",
      align: "center",
      header: (
        <button
          onClick={() => handlePolSort("lifecycle")}
          className="flex w-full items-center justify-center font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
        >
          Lifecycle Status
          <PolSortIcon col="lifecycle" />
        </button>
      ),
      render: (row) => (
        <span
          className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${policyStatusColor[row.lifecycle]}`}
        >
          {policyStatusLabel[row.lifecycle]}
        </span>
      ),
    },
    {
      key: "controlGap",
      align: "center",
      header: (
        <button
          onClick={() => handlePolSort("controlGap")}
          className="flex w-full items-center justify-center font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
        >
          Control Gaps
          <PolSortIcon col="controlGap" />
        </button>
      ),
      render: (row) =>
        row.controlGap > 0 ? (
          <span className="text-xs font-medium text-red-600 dark:text-red-400">
            {row.controlGap} gaps
          </span>
        ) : (
          <span className="text-xs text-emerald-600 dark:text-emerald-400">
            None
          </span>
        ),
    },
  ];

  const sortedCrCols: Column<(typeof changeRequests)[0]>[] = [
    {
      key: "id",
      header: "ID",
      align: "left",
      render: (row) => <span className="font-mono text-xs">{row.id}</span>,
    },
    {
      key: "title",
      align: "left",
      header: (
        <button
          onClick={() => handleCrSort("title")}
          className="flex w-full items-center justify-center font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
        >
          Title
          <CrSortIcon col="title" />
        </button>
      ),
      render: (row) => <>{row.title}</>,
    },
    {
      key: "requester",
      header: "Requester",
      align: "left",
      render: (row) => <>{row.requester}</>,
    },
    {
      key: "type",
      header: "Type",
      align: "left",
      render: (row) => <>{row.type}</>,
    },
    {
      key: "risk",
      align: "center",
      header: (
        <button
          onClick={() => handleCrSort("risk")}
          className="flex w-full items-center justify-center font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
        >
          Risk
          <CrSortIcon col="risk" />
        </button>
      ),
      render: (row) => (
        <StatusBadge status={row.risk as "low" | "medium" | "high"} />
      ),
    },
    {
      key: "status",
      align: "center",
      header: (
        <button
          onClick={() => handleCrSort("status")}
          className="flex w-full items-center justify-center font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
        >
          Status
          <CrSortIcon col="status" />
        </button>
      ),
      render: (row) => (
        <StatusBadge
          status={row.status as "pending" | "in-progress" | "active"}
        />
      ),
    },
    {
      key: "submitted",
      align: "center",
      header: (
        <button
          onClick={() => handleCrSort("submitted")}
          className="flex w-full items-center justify-center font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
        >
          Submitted
          <CrSortIcon col="submitted" />
        </button>
      ),
      render: (row) => <>{row.submitted}</>,
    },
  ];

  const addButtonConfig: Record<GovTab, { label: string; action: () => void }> =
    {
      policies: { label: "New Policy", action: () => setShowNewPolicy(true) },
      workflows: {
        label: "New Workflow",
        action: () => setShowNewWorkflow(true),
      },
      delegation: {
        label: "New Delegation",
        action: () => setShowNewDelegation(true),
      },
      changes: {
        label: "New Change Request",
        action: () => setShowNewChange(true),
      },
    };
  const addBtn = addButtonConfig[activeTab];

  const tabLabels: Record<GovTab, string> = {
    policies: "Policies",
    workflows: "Approval Workflows",
    delegation: "Delegation",
    changes: "Change Requests",
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1>Governance</h1>
          <p className="text-sm d mt-0.5">
            Policies, approval workflows, delegations and change management
          </p>
        </div>
        <button
          onClick={addBtn.action}
          className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity"
        >
          <Plus className="size-4" />
          {addBtn.label}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex overflow-x-auto gap-0 border-b border-border">
        {(Object.entries(tabLabels) as [GovTab, string][]).map(
          ([key, label]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
                activeTab === key
                  ? "border-foreground text-foreground"
                  : "border-transparent d hover:text-foreground"
              }`}
            >
              {label}
            </button>
          ),
        )}
      </div>

      {activeTab === "policies" && (
        <DataTable
          columns={sortedPolicyCols}
          data={sortedPolicies}
          rowKey={(r) => r.id}
        />
      )}

      {activeTab === "workflows" && (
        <div className="space-y-4">
          {workflows.map((wf) => (
            <div
              key={wf.id}
              className="rounded-lg border border-border bg-card p-4"
            >
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex items-start gap-3">
                  <div className="size-9 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                    <Scale className="size-4 d" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{wf.name}</p>
                    <p className="text-xs d mt-0.5">Trigger: {wf.trigger}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {wf.pending > 0 && (
                    <span className="text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-md border border-amber-500/20">
                      {wf.pending} pending
                    </span>
                  )}
                  <StatusBadge status={wf.status as "active"} />
                </div>
              </div>
              <WorkflowStepper
                steps={workflowSteps.slice(0, wf.steps)}
                currentStep={wf.steps - 1}
              />
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                <p className="text-xs d">Approvers:</p>
                {wf.approvers.map((a) => (
                  <span
                    key={a}
                    className="text-xs px-1.5 py-0.5 rounded bg-muted d"
                  >
                    {a}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === "delegation" && (
        <div className="space-y-3">
          {delegations.map((del) => (
            <div
              key={del.id}
              className="rounded-lg border border-border bg-card p-4"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-sm font-medium">{del.role}</p>
                  <p className="text-xs d mt-0.5">
                    Delegated to:{" "}
                    <span className="text-foreground">{del.delegatee}</span>
                  </p>
                  <p className="text-xs d">By: {del.delegator}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs d">Expires: {del.expires}</span>
                  <StatusBadge status={del.status as "active"} />
                </div>
              </div>
              <p className="text-xs d mt-2 pt-2 border-t border-border">
                {del.scope}
              </p>
            </div>
          ))}
        </div>
      )}

      {activeTab === "changes" && (
        <DataTable
          columns={sortedCrCols}
          data={sortedCr}
          rowKey={(r) => r.id}
        />
      )}

      {/* New Policy Modal */}
      <Modal
        open={showNewPolicy}
        onClose={() => setShowNewPolicy(false)}
        title="New Policy"
        description="Create a new governance policy document"
        size="md"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowNewPolicy(false)}
            onSubmit={() => {
              setShowNewPolicy(false);
              setPolicyForm({
                name: "",
                version: "",
                owner: "",
                approver: "",
                reviewDate: "",
              });
            }}
            submitLabel="Save"
            submitDisabled={!policyForm.name || !policyForm.owner}
          />
        }
      >
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Policy Name" required>
            <FormInput
              placeholder="e.g. Information Security Policy"
              value={policyForm.name}
              onChange={(v) => setPolicyForm((f) => ({ ...f, name: v }))}
            />
          </FormField>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Version">
            <FormInput
              placeholder="e.g. 1.0"
              value={policyForm.version}
              onChange={(v) => setPolicyForm((f) => ({ ...f, version: v }))}
            />
          </FormField>
          <FormField label="Next Review Date">
            <FormInput
              type="date"
              value={policyForm.reviewDate}
              onChange={(v) => setPolicyForm((f) => ({ ...f, reviewDate: v }))}
            />
          </FormField>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Policy Owner" required>
            <FormInput
              placeholder="e.g. IT Security"
              value={policyForm.owner}
              onChange={(v) => setPolicyForm((f) => ({ ...f, owner: v }))}
            />
          </FormField>
          <FormField label="Approver">
            <FormSelect
              options={[
                "Alex Johnson",
                "Sarah Kim",
                "Marcus Davis",
                "Dr. Christine Moore",
              ]}
              placeholder="Select approver"
              value={policyForm.approver}
              onChange={(v) => setPolicyForm((f) => ({ ...f, approver: v }))}
            />
          </FormField>
        </div>
      </Modal>

      {/* New Workflow Modal */}
      <Modal
        open={showNewWorkflow}
        onClose={() => setShowNewWorkflow(false)}
        title="New Approval Workflow"
        description="Define a new approval workflow for governance processes"
        size="md"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowNewWorkflow(false)}
            onSubmit={() => {
              setShowNewWorkflow(false);
              setWorkflowForm({ name: "", trigger: "", approvers: "" });
            }}
            submitLabel="Save"
            submitDisabled={!workflowForm.name || !workflowForm.trigger}
          />
        }
      >
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Workflow Name" required>
            <FormInput
              placeholder="e.g. SSP Change Approval"
              value={workflowForm.name}
              onChange={(v) => setWorkflowForm((f) => ({ ...f, name: v }))}
            />
          </FormField>
          <FormField label="Trigger Condition" required>
            <FormInput
              placeholder="e.g. SSP modification submitted"
              value={workflowForm.trigger}
              onChange={(v) => setWorkflowForm((f) => ({ ...f, trigger: v }))}
            />
          </FormField>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormField
            label="Approvers"
            hint="Comma-separated list of roles (e.g. ISSO, System Owner, AO)"
          >
            <FormInput
              placeholder="ISSO, System Owner, AO"
              value={workflowForm.approvers}
              onChange={(v) => setWorkflowForm((f) => ({ ...f, approvers: v }))}
            />
          </FormField>
        </div>
      </Modal>

      {/* New Delegation Modal */}
      <Modal
        open={showNewDelegation}
        onClose={() => setShowNewDelegation(false)}
        title="New Delegation"
        description="Delegate an authority or role to a person or entity"
        size="md"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowNewDelegation(false)}
            onSubmit={() => {
              setShowNewDelegation(false);
              setDelegationForm({
                role: "",
                delegatee: "",
                delegator: "",
                scope: "",
                expires: "",
              });
            }}
            submitLabel="Save"
            submitDisabled={!delegationForm.role || !delegationForm.delegatee}
          />
        }
      >
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Role / Authority" required>
            <FormInput
              placeholder="e.g. Authorizing Official (AO)"
              value={delegationForm.role}
              onChange={(v) => setDelegationForm((f) => ({ ...f, role: v }))}
            />
          </FormField>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Delegated To" required>
            <FormInput
              placeholder="e.g. Dr. Christine Moore"
              value={delegationForm.delegatee}
              onChange={(v) =>
                setDelegationForm((f) => ({ ...f, delegatee: v }))
              }
            />
          </FormField>
          <FormField label="Delegated By">
            <FormInput
              placeholder="e.g. Agency Head"
              value={delegationForm.delegator}
              onChange={(v) =>
                setDelegationForm((f) => ({ ...f, delegator: v }))
              }
            />
          </FormField>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Expiration Date">
            <FormInput
              type="date"
              value={delegationForm.expires}
              onChange={(v) => setDelegationForm((f) => ({ ...f, expires: v }))}
            />
          </FormField>
        </div>
        <FormField label="Scope of Authority">
          <FormTextarea
            placeholder="Describe the scope, limitations, and conditions of this delegation"
            value={delegationForm.scope}
            onChange={(v) => setDelegationForm((f) => ({ ...f, scope: v }))}
          />
        </FormField>
      </Modal>

      {/* New Change Request Modal */}
      <Modal
        open={showNewChange}
        onClose={() => setShowNewChange(false)}
        title="New Change Request"
        description="Submit a change request for review and approval"
        size="lg"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowNewChange(false)}
            onSubmit={() => {
              setShowNewChange(false);
              setChangeForm({
                title: "",
                type: "",
                risk: "",
                requester: "",
                description: "",
              });
            }}
            submitLabel="Submit Request"
            submitDisabled={!changeForm.title || !changeForm.type}
          />
        }
      >
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Change Title" required>
            <FormInput
              placeholder="e.g. Deploy new Vault secrets engine for database credentials"
              value={changeForm.title}
              onChange={(v) => setChangeForm((f) => ({ ...f, title: v }))}
            />
          </FormField>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Change Type" required>
            <FormSelect
              options={[
                "Configuration",
                "Access Control",
                "Security",
                "Network",
                "Maintenance",
                "Emergency",
                "Software",
                "Hardware",
              ]}
              placeholder="Select type"
              value={changeForm.type}
              onChange={(v) => setChangeForm((f) => ({ ...f, type: v }))}
            />
          </FormField>
          <FormField label="Risk Level">
            <FormSelect
              options={["low", "medium", "high", "critical"]}
              placeholder="Assess risk"
              value={changeForm.risk}
              onChange={(v) => setChangeForm((f) => ({ ...f, risk: v }))}
            />
          </FormField>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Requester">
            <FormSelect
              options={[
                "Alex Johnson",
                "Sarah Kim",
                "Marcus Davis",
                "Database Team",
                "Platform Team",
                "Network Team",
              ]}
              placeholder="Select requester"
              value={changeForm.requester}
              onChange={(v) => setChangeForm((f) => ({ ...f, requester: v }))}
            />
          </FormField>
        </div>
        <FormField label="Description & Justification">
          <FormTextarea
            rows={4}
            placeholder="Describe the change, business justification, rollback plan, and any potential security impact"
            value={changeForm.description}
            onChange={(v) => setChangeForm((f) => ({ ...f, description: v }))}
          />
        </FormField>
      </Modal>
    </div>
  );
}
